import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import { brands } from '../data';

const IMG = `${process.env.PUBLIC_URL}/images`;

const orbitSymbols = {
  atlhea: 'camera',
  epanchang: 'lightbulb',
  'marriage-colours': 'heart',
  anantmaya: 'leaf',
  happiffie: 'users',
};

const pillars = [
  {
    icon: 'users',
    title: 'Shared Purpose',
    desc: 'Five brands, one purpose of spreading joy and impact.',
  },
  {
    icon: 'eye',
    title: 'Unified Vision',
    desc: 'Building meaningful experiences that enrich lives.',
  },
  {
    icon: 'cpu',
    title: 'Technology First',
    desc: 'Leveraging AI and innovation to create intelligent solutions.',
  },
  {
    icon: 'heart',
    title: 'Human at Heart',
    desc: 'Empathy, trust and relationships drive everything we do.',
  },
  {
    icon: 'handshake',
    title: 'Better Together',
    desc: 'Seamless synergy to create exponential positive impact.',
  },
];

const stats = [
  { icon: 'users', value: '1.2M+', label: 'Experiences Enabled' },
  { icon: 'briefcase', value: '75K+', label: 'Vendors Empowered' },
  { icon: 'map-pin', value: '500+', label: 'Cities Reached' },
  { icon: 'smile', value: '4.8M+', label: 'Happy Customers' },
  { icon: 'coins', value: '₹2500Cr+', label: 'Economic Impact Created' },
  { icon: 'award', value: '10+', label: 'Years of Innovation' },
];

export default function Brands() {
  return (
    <div className="brands-page">
      {/* =================== HERO =================== */}
      <section className="bp-hero">
        <div
          className="bp-hero__bg"
          style={{ backgroundImage: `url(${IMG}/banner.png)` }}
        />
        <div className="container container--wide bp-hero__inner">
          <div className="crumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>Brands</span>
          </div>

          <div className="bp-hero__row">
            <div className="bp-hero__copy">
              <h1 className="bp-hero__title">
                Different Worlds.
                <br />
                <span className="grad-pink">One Unified Vision.</span>
              </h1>
              <p className="bp-hero__lead">
                Five purpose-led brands working together to create meaningful
                impact across celebrations, relationships, spirituality and
                everyday life.
              </p>
              <div className="bp-hero__actions">
                <Link to="#universe" className="btn btn--primary">
                  Explore the Ecosystem Map <Icon name="arrow-right" size={18} />
                </Link>
                <button type="button" className="btn btn--ghost">
                  <Icon name="play" size={14} /> Our Brand Philosophy
                </button>
              </div>
            </div>

            <div className="bp-hero__visual">
              <div className="bp-orbit">
                <span className="bp-orbit__ring bp-orbit__ring--1" />
                <span className="bp-orbit__ring bp-orbit__ring--2" />
                <div className="bp-orbit__core">
                  <img src={`${IMG}/Logo-caladium.png`} alt="Caladium Systems" />
                </div>
                {brands.map((b) => (
                  <div
                    key={b.id}
                    className={`bp-node bp-node--${b.orbitPos}`}
                    style={{ '--c': b.color }}
                  >
                    <div className="bp-node__badge">
                      <img
                        className="bp-node__logo"
                        src={b.logo}
                        alt={b.name}
                      />
                      <span className="bp-node__tag">{b.orbitLabel}</span>
                    </div>
                    <span className="bp-node__symbol">
                      <Icon name={orbitSymbols[b.id]} size={14} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =================== BRAND UNIVERSE =================== */}
      <section className="section bp-universe" id="universe">
        <div className="container container--wide">
          <header className="section__head bp-head">
            <div className="bp-divider">
              <span className="bp-divider__line bp-divider__line--l" />
              <h2 className="h2 bp-divider__title">Our Brand Universe</h2>
              <span className="bp-divider__line bp-divider__line--r" />
            </div>
            <p className="section__sub">
              Explore our five purpose-led brands and discover how each one
              creates meaningful impact in its own unique way.
            </p>
          </header>

          <div className="bp-grid">
            {brands.map((b) => (
              <article
                key={b.id}
                className="bp-card"
                style={{ '--c': b.color }}
              >
                <div className="bp-card__head">
                  {b.logo ? (
                    <img className="bp-card__logo" src={b.logo} alt={b.name} />
                  ) : (
                    <span className="bp-card__word">{b.name}</span>
                  )}
                  <span className="bp-card__tag">{b.tagline}</span>
                </div>

                <div className="bp-card__media">
                  <img src={b.photo} alt={b.name} />
                </div>

                <div className="bp-card__body">
                  <p className="bp-card__desc">{b.blurb}</p>
                  <ul className="bp-features">
                    {b.features.map((f) => (
                      <li key={f}>
                        <span className="bp-features__dot">
                          <Icon name="check" size={13} />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/" className="bp-card__cta">
                    Explore {b.name} <Icon name="arrow-right" size={15} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =================== WHY STAND TOGETHER =================== */}
      <section className="bp-together">
        <div className="container container--wide">
          <div className="bp-together__panel">
            {pillars.map((p) => (
              <div className="bp-pillar" key={p.title}>
                <span className="bp-pillar__icon">
                  <Icon name={p.icon} size={28} />
                </span>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== STATS =================== */}
      <section className="bp-stats-sec">
        <div className="container container--wide">
          <div className="bp-stats">
            {stats.map((s) => (
              <div className="bp-stat" key={s.label}>
                <span className="bp-stat__icon">
                  <Icon name={s.icon} size={24} />
                </span>
                <div className="bp-stat__text">
                  <strong className="bp-stat__value">{s.value}</strong>
                  <span className="bp-stat__label">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
